import { authBreadcrumb, captureAuthError } from '@features/sentry-connect';
import { FirestoreUserData } from '@shared/types';
import { GUser, Timestamp } from 'blooksy-backend';
import {
  DocumentData,
  QueryDocumentSnapshot,
  SnapshotOptions,
  FirestoreDataConverter,
  PartialWithFieldValue,
} from 'firebase/firestore';

const toMillisOrNull = (value: Timestamp | null | undefined): number | null => {
  if (!value || typeof value.toMillis !== 'function') return null;
  return value.toMillis();
};

export const firestoreUserConverter: FirestoreDataConverter<FirestoreUserData> = {
  toFirestore(data: PartialWithFieldValue<FirestoreUserData>): DocumentData {
    return data;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): FirestoreUserData {
    const { subscriptionInfo, ...data } = snapshot.data(options) as GUser<Timestamp>;
    if (!subscriptionInfo) {
      return { ...data, subscriptionInfo: null };
    }
    try {
      const { currentPeriodEnd, currentPeriodStart, cancelAt, canceledAt, endedAt, created } = subscriptionInfo;
      const createdMs = toMillisOrNull(created);
      if (createdMs === null) {
        authBreadcrumb('userConverter: subscriptionInfo.created missing/invalid', {
          uid: snapshot.id,
          createdType: typeof created,
        });
      }
      return {
        ...data,
        subscriptionInfo: {
          ...subscriptionInfo,
          currentPeriodStart: toMillisOrNull(currentPeriodStart),
          currentPeriodEnd: toMillisOrNull(currentPeriodEnd),
          cancelAt: toMillisOrNull(cancelAt),
          canceledAt: toMillisOrNull(canceledAt),
          endedAt: toMillisOrNull(endedAt),
          created: createdMs as number,
        },
      };
    } catch (error: unknown) {
      captureAuthError(error, 'userConverter:fromFirestore', {
        uid: snapshot.id,
        subscriptionKeys: Object.keys(subscriptionInfo ?? {}).join(','),
      });
      throw error;
    }
  },
};
